import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import TextArea from '@wix/da-ds/pkg/formControls/dsPresets/TextArea';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import ImageIcon from '@wix/da-ds/pkg/Icons/24x24/AddImage';
import TrashcanIcon from '@wix/da-ds/pkg/Icons/24x24/Trashcan';
import CloseIcon from '@wix/da-ds/pkg/Icons/24x24/Close';
import MediaSelector from '@wix/da-shared-react/pkg/MediaPopup/MediaSelector';
import { MediaSourceType } from '@wix/da-shared-react/pkg/MediaPopup/types';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import { PapiDeviation, PapiUser } from '@wix/da-papi-types';
import DeviationMediaService from '@wix/da-shared-react/pkg/utils/DeviationMediaService';
import { isDeviationAuthor } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { isSafeForMuroAndVideoCreator } from '@wix/da-shared-react/pkg/ActionBar/ElementPlacementContext/utils';
import { getCurrentUser } from '@wix/da-shared-react/pkg/publicSession/selectors';
import ConfirmationModal from '@wix/da-shared-react/pkg/Popup/ConfirmationModal';
import { fetchDeviation } from '@wix/da-shared-react/pkg/redux/deviations/actionCreators';
import {
  getVideoCreatorPrompt,
  getVideoCreatorReferenceDeviation,
  getVideoCreatorErrorCode,
} from '../../../../selectors/studio';
import {
  updateVideoCreatorPrompt,
  updateVideoCreatorReferenceDeviation,
} from '../../../../actions/studio/videoCreator';
import { VideoCreatorErrorCode } from '../../../../types/store';
import { getBabelKeyFromErrorCode } from '../../errorHandling';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import VideoPointsBalance from '../../../../components/StudioHeader/sections/PlaygroundHeader/VideoPointsBalance';
import {
  BiData,
  AiToolImageRightsConfirmClickBiEvent,
  AiToolAddImageClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';

import s from './Prompt.scss';
import sControl from '../VideoCreatorControls.scss';

const MAX_PROMPT_LENGTH = 400;

const validateDeviation = (
  deviation: PapiDeviation,
  currentUser: PapiUser
): boolean => {
  const isAuthor = isDeviationAuthor(deviation, currentUser);
  const isOwner = deviation.adoptable?.owner.userId === currentUser.userId;
  if (!isAuthor && !isOwner) {
    return false;
  }

  return isSafeForMuroAndVideoCreator(deviation);
};

export interface Props {}

const Prompt: FC<Props> = () => {
  const { t } = useTranslation();
  const logBiEvent = useBiLogger();
  const isMobile = useContext(MobileContext);
  const dispatch = useDispatch();
  const prompt = useSelector(getVideoCreatorPrompt);
  const errorCode = useSelector(getVideoCreatorErrorCode);
  const hasError =
    errorCode !== VideoCreatorErrorCode.NO_ERROR &&
    errorCode !== VideoCreatorErrorCode.INSUFFICIENT_POINTS_BALANCE;

  const referenceDeviation = useSelector(getVideoCreatorReferenceDeviation);
  const currentUser = useSelector(getCurrentUser);
  const [isMediaSelectorVisible, setIsMediaSelectorVisible] = useState(false);
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] =
    useState(false);
  const hasProcessedUrlParams = useRef(false);

  const referenceDeviationThumb = referenceDeviation
    ? DeviationMediaService.getSmallestThumb(referenceDeviation)
    : undefined;

  const errorMessage = useMemo(() => {
    if (errorCode === VideoCreatorErrorCode.NO_ERROR) {
      return null;
    }
    const key = getBabelKeyFromErrorCode(errorCode);
    if (key) {
      return t(key);
    } else {
      return null;
    }
  }, [t, errorCode]);

  const showMediaSelector = useCallback(() => {
    setIsMediaSelectorVisible(true);
  }, []);

  const hideMediaSelector = useCallback(() => {
    setIsMediaSelectorVisible(false);
  }, []);

  const showConfirmationModal = useCallback(() => {
    logBiEvent(
      BiData<AiToolAddImageClickBiEvent>({
        evid: 924,
      })
    );
    setIsConfirmationModalVisible(true);
  }, [logBiEvent]);

  const handleImageIconClick = useCallback(() => {
    if (referenceDeviation) {
      dispatch(updateVideoCreatorReferenceDeviation(undefined));
      return;
    }
    showConfirmationModal();
  }, [referenceDeviation, dispatch, showConfirmationModal]);

  const handleImageLabelClick = useCallback(
    (e: React.MouseEvent<HTMLLabelElement>) => {
      e.preventDefault();
      e.stopPropagation();
      showConfirmationModal();
    },
    [showConfirmationModal]
  );

  const hideConfirmationModal = useCallback(() => {
    setIsConfirmationModalVisible(false);
  }, []);

  const handleConfirm = useCallback(() => {
    logBiEvent(
      BiData<AiToolImageRightsConfirmClickBiEvent>({
        evid: 925,
      })
    );
    hideConfirmationModal();
    showMediaSelector();
  }, [logBiEvent, hideConfirmationModal, showMediaSelector]);

  const handleImageSelect = useCallback(
    (selectedDeviations: PapiDeviation[]) => {
      const selectedDeviation = selectedDeviations[0];
      if (selectedDeviation) {
        if (validateDeviation(selectedDeviation, currentUser)) {
          dispatch(updateVideoCreatorReferenceDeviation(selectedDeviation));
        }
      }
      hideMediaSelector();
    },
    [hideMediaSelector, dispatch, currentUser]
  );

  const handlePromptChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      dispatch(updateVideoCreatorPrompt(e.target.value));
    },
    [dispatch]
  );

  const addImageIcon = useMemo(
    () => (
      <div
        className={cn(
          s['add-image-icon'],
          referenceDeviationThumb && s['has-reference-thumb']
        )}
      >
        {referenceDeviationThumb ? (
          <>
            <img
              className={s['thumb-image']}
              src={referenceDeviationThumb.src}
              alt={referenceDeviation?.title ?? ''}
            />
            <span className={s['thumb-trash-overlay']} aria-hidden>
              <TrashcanIcon size={IconSize.MEDIUM} />
            </span>
          </>
        ) : (
          <ImageIcon size={IconSize.MEDIUM} />
        )}
      </div>
    ),
    [referenceDeviationThumb, referenceDeviation?.title]
  );

  useEffect(() => {
    if (hasProcessedUrlParams.current) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    let shouldUpdateUrl = false;

    const promptFromUrl = urlParams.get('prompt');
    if (promptFromUrl && prompt.length === 0) {
      const trimmedPrompt = promptFromUrl.substring(0, MAX_PROMPT_LENGTH);
      dispatch(updateVideoCreatorPrompt(trimmedPrompt));
      urlParams.delete('prompt');
      shouldUpdateUrl = true;
    }

    const deviationIdFromUrl = urlParams.get('deviationid');
    if (deviationIdFromUrl && !referenceDeviation) {
      // There are two types of deviation IDs (base 10 and 36). Deal with both.
      const hasNonBase10Chars = /[a-zA-Z]/.test(deviationIdFromUrl);
      const base = hasNonBase10Chars ? 36 : 10;
      const deviationId = parseInt(deviationIdFromUrl, base);
      if (!isNaN(deviationId)) {
        dispatch(
          fetchDeviation({
            deviationid: deviationId,
            type: 'art',
            onSuccess: (deviation: PapiDeviation) => {
              if (!validateDeviation(deviation, currentUser)) {
                return;
              }

              dispatch(updateVideoCreatorReferenceDeviation(deviation));
            },
          })
        );
        urlParams.delete('deviationid');
        shouldUpdateUrl = true;
      }
    }

    if (shouldUpdateUrl) {
      const newUrl = urlParams.toString()
        ? `${window.location.pathname}?${urlParams.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }

    hasProcessedUrlParams.current = true;
  }, [prompt, referenceDeviation, dispatch, currentUser]);

  return (
    <div
      className={cn(
        s['root'],
        hasError && s['has-error'],
        isMobile && s['mobile'],
        isMobile && referenceDeviationThumb && s['mobile-has-reference']
      )}
    >
      {!isMobile && (
        <label
          className={sControl['control-label']}
          htmlFor="video-creator-prompt"
        >
          {t('studio.videoCreator.label.prompt')}
        </label>
      )}
      {isMobile && <VideoPointsBalance className={s['points-balance']} />}
      {isMobile && (
        <button
          id={'add-image-button'}
          className={cn(
            s['add-image-button'],
            referenceDeviationThumb && s['has-reference']
          )}
          onClick={handleImageIconClick}
          aria-label={t('studio.videoCreator.label.upload')}
        >
          {addImageIcon}
          {referenceDeviationThumb && (
            <span className={s['mobile-remove-icon']} aria-hidden>
              <CloseIcon size={IconSize.SMALLER} />
            </span>
          )}
        </button>
      )}
      <div className={s['content']}>
        <TextArea
          className={s['prompt-area']}
          wrapperClassName={s['prompt-area-wrapper']}
          id={'video-creator-prompt'}
          label={t('studio.videoCreator.label.prompt')}
          maxLength={MAX_PROMPT_LENGTH}
          isLabelHidden={true}
          placeholder={t('studio.videoCreator.promptPlaceholder')}
          value={prompt}
          noBottomSpacing={true}
          showCounter={false}
          onChange={handlePromptChange}
        />
        {!isMobile && (
          <div className={s['file-upload-area']}>
            <button
              id={'add-image-button'}
              className={s['add-image-button']}
              onClick={handleImageIconClick}
            >
              {addImageIcon}
              <label
                className={s['add-image-label']}
                onClick={handleImageLabelClick}
              >
                {t('studio.videoCreator.label.upload')}
              </label>
            </button>
          </div>
        )}
        {hasError && errorMessage && (
          <div className={s['error-message']}>{errorMessage}</div>
        )}
      </div>
      <ConfirmationModal
        isOpen={isConfirmationModalVisible}
        title={t('studio.videoCreator.imageConfirm.title')}
        text={t('studio.videoCreator.imageConfirm.text')}
        confirmBtnLabel={t('studio.videoCreator.imageConfirm.confirm')}
        cancelBtnLabel={t('studio.videoCreator.imageConfirm.cancel')}
        onConfirm={handleConfirm}
        onCancel={hideConfirmationModal}
        onRequestClose={hideConfirmationModal}
        className={s['confirmation-modal']}
      />
      {isMediaSelectorVisible && (
        <MediaSelector
          mediaSourceTypes={[MediaSourceType.GALLERY, MediaSourceType.STASH]}
          allowedDeviationTypes={[DeviationTypes.IMAGE]}
          allowGifs={false}
          allowPremium={false}
          allowMature={false}
          onClose={hideMediaSelector}
          onSelect={handleImageSelect}
        />
      )}
    </div>
  );
};

Prompt.displayName = 'Prompt';
export default Prompt;
